import { useState, useMemo } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Menu,
} from "@mui/material";
import { format } from "date-fns";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import ZohoSyncStatus from "../../components/ZohoSyncStatus/ZohoSyncStatus";
import { useNavigate } from "react-router-dom";
import { STATUS_CONFIG } from "../../constant";

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  ...["Scheduled", "Completed", "InProgress", "LinkExpired"].map((key) => ({
    value: key,
    label: STATUS_CONFIG[key]?.label ?? key,
  })),
];

/* ============================
   HELPERS
============================ */

const formatDate = (date) =>
  date ? format(new Date(date), "MM/dd/yyyy") : null;

const formatScore = (value) =>
 Math.floor(value * 10) / 10;

/* ============================
   COMPONENT
============================ */

const CandidateInterviewsTable = ({ interviews, loading, onReschedule }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [selectedRowForActions, setSelectedRowForActions] = useState(null);

  const allRows = interviews?.interviews || [];

  /* ============================
     FILTER + SORT + PAGINATION
  ============================ */

  const filteredRows = useMemo(() => {
    return statusFilter
      ? allRows.filter((row) => row.status === statusFilter)
      : allRows;
  }, [allRows, statusFilter]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.field) return filteredRows;

    const { field, direction } = sortConfig;
    const factor = direction === "asc" ? 1 : -1;

    return [...filteredRows].sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];

      if (!aVal && !bVal) return 0;
      if (!aVal) return 1 * factor;
      if (!bVal) return -1 * factor;

      return (
        (new Date(aVal).getTime() - new Date(bVal).getTime()) * factor
      );
    });
  }, [filteredRows, sortConfig]);

  const paginatedRows = useMemo(() => {
    return sortedRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedRows, page, rowsPerPage]);

  /* ============================
     HANDLERS
  ============================ */

  const handleSortByDate = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  const handleOpenActionsMenu = (event, row) => {
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedRowForActions(row);
  };

  const handleCloseActionsMenu = () => {
    setActionMenuAnchorEl(null);
    setSelectedRowForActions(null);
  };

  const handleRescheduleClick = () => {
    if (selectedRowForActions) onReschedule(selectedRowForActions);
    handleCloseActionsMenu();
  };

  /* ============================
     TABLE BODY STATES
  ============================ */

  const renderLoading = () => (
    <TableRow>
      <TableCell colSpan={10}>
        <Box
          sx={{
            py: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={40} thickness={4} sx={{ color: "primary.main" }} />
          <Typography sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
            Loading candidate interviews…
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderEmpty = (text) => (
    <TableRow>
      <TableCell colSpan={10}>
        <Typography
          sx={{
            fontWeight: 500,
            color: theme.palette.text.secondary,
            fontSize: "0.9375rem",
          }}
        >
          {text}
        </Typography>
      </TableCell>
    </TableRow>
  );

  /* ============================
     RENDER
  ============================ */

  return (
    <Box sx={{ mt: 3 }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* ACTION MENU */}
        <Menu
          anchorEl={actionMenuAnchorEl}
          open={Boolean(actionMenuAnchorEl)}
          onClose={handleCloseActionsMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              minWidth: 100,
              maxWidth: 150,        
            },
          }}
        >
          <MenuItem
            onClick={handleRescheduleClick}
            dense
            sx={{ fontSize: "0.8125rem", px: 1.5 }}
          >
            <CalendarTodayOutlinedIcon
              fontSize="small"
              sx={{ mr: 1, fontSize: 16 }}
            />
            Reschedule
          </MenuItem>
        </Menu>

        {/* HEADER */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            All Candidate Details
          </Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="status-filter-label" shrink>
              Status
            </InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={statusFilter}
              label="Status"
              size="small"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              displayEmpty
              renderValue={(v) =>
                STATUS_FILTER_OPTIONS.find((o) => o.value === v)?.label ?? "All"
              }
              sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 1.5,
                fontSize: "0.875rem",
              }}
            >
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value} size="small" sx={{ fontSize: "0.875rem" }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel shrink>Status</InputLabel>
            <Select
              value={statusFilter}
              //label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
        </Box>

        {/* TABLE */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  { label: "Candidate Name" },
                  { label: "Job Title" },
                  { label: "Status" },
                  { label: "AI Behavior Score" },
                  { label: "AI Response Score" },
                  { label: "Scheduled Date", sortField: "scheduledDate" },
                  { label: "Completed Date", sortField: "completedAt" },
                  { label: "Recording" },
                  { label: "Zoho Sync" },
                  { label: "Actions" },
                ].map((col) => (
                  <TableCell
                    key={col.label}
                    onClick={() =>
                      col.sortField && handleSortByDate(col.sortField)
                    }
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: "0.875rem",
                      cursor: col.sortField ? "pointer" : "default",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading
                ? renderLoading()
                : filteredRows.length === 0
                ? renderEmpty("No candidates match the selected status.")
                : paginatedRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 500,textTransform: "capitalize" }}>
                        {row.candidateName || "-"}
                      </TableCell>

                      <TableCell >{row.jobTitle || "-"}</TableCell>

                      <TableCell>
                        <StatusBadge status={row.status} />
                      </TableCell>

                      <TableCell>{formatScore(row.behaviourScore)}</TableCell>
                      <TableCell>{formatScore(row.answereScore)}</TableCell>

                      <TableCell>
                        {formatDate(row.scheduledDate) || "Not Scheduled"}
                      </TableCell>

                      <TableCell>
                        {formatDate(row.completedAt) ? (
                          formatDate(row.completedAt)
                        ) : (
                          <span style={{ color: "#a6aebb" }}>
                            Not Completed
                          </span>
                        )}
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="text"
                          startIcon={<PlayCircleOutlineIcon />}
                          disabled={row.status !== "Completed"}
                          sx={{
                            textTransform: "none",
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                          }}
                          onClick={() =>
                            navigate(`/view-recording/${row.id}`, {
                              state: { candidate: row },
                            })
                          }
                        >
                          View
                        </Button>
                      </TableCell>

                      <TableCell>
                        {row.zohoSyncStatus ? (
                          <ZohoSyncStatus status={row.zohoSyncStatus} />
                        ) : (
                          <span style={{ color: "#a6aebb" }}>
                            Not Synced
                          </span>
                        )}
                      </TableCell>

                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenActionsMenu(e, row)}
                          disabled={
                            row.status !== "Scheduled" &&
                            row.status !== "LinkExpired"
                          }
                          sx={{
                            color:
                              row.status === "Scheduled" ||
                              row.status === "LinkExpired"
                                ? theme.palette.text.primary
                                : theme.palette.text.disabled,
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* PAGINATION */}
        {filteredRows.length > 0 && (
          <TablePagination
  component="div"
  count={filteredRows.length}
  page={page}
  onPageChange={(_, p) => setPage(p)}
  rowsPerPage={rowsPerPage}
  onRowsPerPageChange={(e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  }}
  rowsPerPageOptions={[5, 10, 25]}   // ✅ ADD THIS
/>

        )}
      </Paper>
    </Box>
  );
};

export default CandidateInterviewsTable;
